import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { RiExternalLinkLine } from "@remixicon/react";
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useError } from "../../contexts/ErrorContext";
import { useShippingDataCoordinator } from "../../hooks/useShippingDataCoordinator";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import DisplayExportJobAnalysis from "../UI/DisplayExportJobAnalysis";
import DownloadS3URIButton from "../UI/DownloadS3URIButton";

import RefetchingTimestamp from "../../components/UI/RefetchingTimestamp";
import {
  formatDateYYYYMMDD,
  formatFileSize,
  formatNumber,
  formatTimestampHumanReadableTZ,
} from "utils/formatting";
import { formatDurationFromSecs } from "utils/formatting/timeFormatters";
import { Box } from "@mui/material";
import { ShippingDetailRecord_T } from "../../types/Shipping";

interface ShippingStatusDetailsTableProps {
  dateStart: string;
  dateEnd: string;
  shippingDetails?: ShippingDetailRecord_T[] | null;
  isLoading?: boolean;
  showUpdateTimestamp?: boolean;
  onUpdateTimestamp?: (timestamp: number | null) => void;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "delivery_id", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 50, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const columns_def: GridColDef[] = [
  {
    field: "export_s3_uri",
    headerName: "Download Export",
    width: 90,
    renderCell: (params) =>
      params.value ? <DownloadS3URIButton s3Uri={params.value} /> : null,
  },
  {
    field: "export_job_id",
    headerName: "View Export Details",
    width: 90,
    renderCell: (params) =>
      params.value ? (
        <DisplayExportJobAnalysis exportJobId={params.value} />
      ) : null,
  },
  {
    field: "delivery_id",
    headerName: "Delivery ID",
    type: "string",
    width: 100,
  },
  {
    field: "delivery_name",
    headerName: "Delivery Name",
    width: 150,
    renderCell: (params: GridRenderCellParams) => (
      <Link
        to={`/delivery/${params.row.delivery_id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {params.row.delivery_name} <RiExternalLinkLine size="14" />
      </Link>
    ),
  },
  {
    field: "customer_name",
    headerName: "Customer",
    width: 100,
    renderCell: (params: GridRenderCellParams) => (
      <Link
        to={`/customer/${params.row.customer_id}/deliveries`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {params.row.customer_name} <RiExternalLinkLine size="14" />
      </Link>
    ),
  },
  {
    field: "fulfillment_group_name",
    headerName: "Fulfillment Group(s)",
    width: 140,
  },
  { field: "fulfillment_status", headerName: "Status", width: 200 },
  {
    field: "status_last_updated",
    headerName: "Last Updated",
    type: "dateTime",
    width: 200,
    valueGetter: (value) => {
      return value ? new Date(value) : null;
    },
    renderCell: (params: GridRenderCellParams) =>
      params.value
        ? formatTimestampHumanReadableTZ(params.value.toISOString(), "UTC")
        : "",
  },
  {
    field: "volume_upgrade_scheduled",
    headerName: "Volume Upgrade?",
    width: 100,
    type: "boolean",
  },
  {
    field: "volume",
    headerName: "Volume",
    type: "number",
    width: 100,
    renderCell: (params: GridRenderCellParams<Number>) =>
      formatNumber(params.value),
  },
  {
    field: "previous_volume",
    headerName: "Prev Volume",
    type: "number",
    width: 100,
    renderCell: (params: GridRenderCellParams<Number>) =>
      formatNumber(params.value),
  },
  {
    field: "export_duration",
    headerName: "Export Duration",
    type: "number",
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? formatDurationFromSecs(params.value) : "",
  },
  {
    field: "shipping_duration",
    headerName: "Shipping Duration",
    type: "number",
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? formatDurationFromSecs(params.value) : "--",
  },
  {
    field: "completion_signal",
    headerName: "Completion Signal",
    width: 100,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? params.value : "--",
  },
  {
    field: "target_fulfillment_date",
    headerName: "Target Fulfillment Date",
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? formatDateYYYYMMDD(new Date(params.value)) : "",
  },
  {
    field: "fulfillment_destination",
    headerName: "Fulfillment Destination",
    width: 100,
  },
  {
    field: "destination_uri",
    headerName: "Destination URI",
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? params.value : "--",
  },
  {
    field: "file_type",
    headerName: "File Type",
    width: 100,
  },
  {
    field: "file_size_kb",
    headerName: "File Size",
    width: 100,
    type: "number",
    renderCell: (params: GridRenderCellParams) => formatFileSize(params.value),
  },
  {
    field: "previous_file_size_kb",
    headerName: "Prev File Size",
    width: 100,
    type: "number",
    renderCell: (params: GridRenderCellParams) => formatFileSize(params.value),
  },
  {
    field: "export_id",
    headerName: "Export ID",
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.row.export_id ? (
        <Link
          to={`https://kuebiko.dataplor.com/deliveries/${params.row.delivery_id}/exports/${params.row.export_id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {params.value} <RiExternalLinkLine size="14" />
        </Link>
      ) : (
        "--"
      ),
  },
  {
    field: "fgs_id",
    headerName: "FG Shipment ID",
    width: 150,
    renderCell: (params: GridRenderCellParams) =>
      params.row.fgs_id ? (
        <Link
          to={`https://kuebiko.dataplor.com/customers/${params.row.customer_id}/fulfillment-groups/${params.row.fulfillment_group_id}/fulfillment-group-shipments/${params.row.fgs_id}/data`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {params.row.fgs_id} <RiExternalLinkLine size="14" />
        </Link>
      ) : (
        "--"
      ),
  },
  {
    field: "countries",
    headerName: "Countries",
    width: 200,
    renderCell: (params: GridRenderCellParams) => (
      <>
        {params.row.countries.map(
          (country: { id: string; name: string }, index: number) => (
            <Link
              key={country.id}
              to={`https://kuebiko.dataplor.com/countries/${country.id}/data`}
            >
              {country.name}
              {index < params.row.countries.length - 1 && ", "}
            </Link>
          )
        )}
        <RiExternalLinkLine size="14" />
      </>
    ),
  },
];

const ShippingStatusDetailsTable: React.FC<ShippingStatusDetailsTableProps> = ({
  dateStart,
  dateEnd,
  shippingDetails: propsShippingDetails,
  isLoading: propsIsLoading,
  showUpdateTimestamp = false,
  onUpdateTimestamp,
}) => {
  const { showError } = useError();

  const {
    shippingDetails: localShippingDetails,
    dataUpdatedAt,
    detailsError,
    isLoading: localIsLoading,
    shippingDetailsRefetching: isRefetching,
  } = useShippingDataCoordinator(dateStart, dateEnd);

  const shippingDetails = propsShippingDetails ?? localShippingDetails;
  const isLoading = propsIsLoading ?? localIsLoading;

  useEffect(() => {
    if (onUpdateTimestamp && dataUpdatedAt) {
      onUpdateTimestamp(dataUpdatedAt);
    }
  }, [dataUpdatedAt, onUpdateTimestamp]);

  useEffect(() => {
    if (detailsError) {
      showError(
        `Failed to get shipping details for dates: ${dateStart} - ${dateEnd}`
      );
    }
  }, [detailsError, showError, dateStart, dateEnd]);

  return (
    <Box sx={{ minHeight: "800px" }}>
      <GaneshDataGrid
        loading={isLoading}
        data={shippingDetails || []}
        columns={columns_def}
        gridId="ShippingDetailsGrid"
        defaultGridState={defaultGridState}
      />

      {showUpdateTimestamp && dataUpdatedAt && (
        <Box sx={{ mt: 2, width: "100%", textAlign: "center" }}>
          <RefetchingTimestamp
            isRefetching={isRefetching}
            updatedAtTimestamp={dataUpdatedAt}
          />
        </Box>
      )}
    </Box>
  );
};

export default ShippingStatusDetailsTable;
